import { debounce } from 'lodash';
import {
  Cursor,
  GraphComponent,
  GraphItemTypes,
  HoveredItemChangedEventArgs,
  IEdge,
  ILabel,
  IModelItem,
  INode,
  ItemHoverInputMode,
} from '@ardoq/yfiles';
import { GraphItem } from '@ardoq/graph';
import {
  getConnectedNodesAndEdges,
  resetNodesAndEdgesConnectedStyles,
  updateNodesAndEdgesConnectednessToSelection,
} from 'yfilesExtensions/graphSelection';
import { HoverModeArgs } from './types';

export const installHoverMode = (
  graphComponent: GraphComponent,
  { edgeHover, highlightAdjacent, transparentizeNonAdjacent }: HoverModeArgs
) => {
  const hoverMode = new ArdoqHoverInputMode();

  hoverMode.exclusive = false;
  hoverMode.enabled = true;
  hoverMode.hoverItems =
    GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL;
  hoverMode.hoverCursor = Cursor.POINTER;
  hoverMode.ignoreInvalidItems = true;

  hoverMode.addEventListener(
    'hovered-item-changed',
    debounce(({ item }: HoveredItemChangedEventArgs) => {
      const { highlights, graph } = graphComponent;
      highlights.clear();
      const owner = item instanceof ILabel ? item.owner : item;
      // The debounce can fire after the hovered item was removed (e.g. graph rebuild).
      if (owner === null || !graph.contains(owner)) {
        updateNodesAndEdgesConnectednessToSelection(graphComponent);
        return;
      }
      resetNodesAndEdgesConnectedStyles(graph);

      if (owner instanceof INode) {
        if (transparentizeNonAdjacent) {
          nodeHoverTransparentize(owner, graphComponent);
        }
        if (highlightAdjacent) {
          const adjacentEdges = graph.edgesAt(owner);
          adjacentEdges.forEach(edge => {
            highlights.add(edge);
          });
        }

        graphComponent.invalidate();
      }

      if (edgeHover && owner instanceof IEdge) {
        if (transparentizeNonAdjacent) {
          edgeHoverTransparentize(owner, graphComponent);
        }
        highlights.add(owner);
        graphComponent.invalidate();
      }
    }, 100)
  );

  function nodeHoverTransparentize(
    node: INode,
    graphComponent: GraphComponent
  ) {
    const { connectedNodes: adjacentNodes, connectedEdges: adjacentEdges } =
      getConnectedNodesAndEdges(graphComponent.graph, [node], []);

    // Group membership implies a visual connection — keep all descendants visible.
    if (graphComponent.graph.isGroupNode(node)) {
      graphComponent.graph.groupingSupport
        .getDescendants(node)
        .forEach(descendant => adjacentNodes.add(descendant));
    }

    applyTransparencyToNonAdjacentNodes(graphComponent, adjacentNodes);
    applyTransparencyToNonAdjacentEdges(graphComponent, adjacentEdges);
  }

  function edgeHoverTransparentize(
    edge: IEdge,
    graphComponent: GraphComponent
  ) {
    const { connectedNodes: adjacentNodes, connectedEdges: adjacentEdges } =
      getConnectedNodesAndEdges(graphComponent.graph, [], [edge]);

    applyTransparencyToNonAdjacentNodes(graphComponent, adjacentNodes);
    applyTransparencyToNonAdjacentEdges(graphComponent, adjacentEdges);
  }

  function applyTransparencyToNonAdjacentNodes(
    graphComponent: GraphComponent,
    adjacentNodes: Set<INode>
  ) {
    graphComponent.graph.nodes.forEach(node => {
      if (!adjacentNodes.has(node) && node.tag instanceof GraphItem) {
        node.tag.isTransparentized = true;
      }
    });
  }
  function applyTransparencyToNonAdjacentEdges(
    graphComponent: GraphComponent,
    adjacentEdges: Set<IEdge>
  ) {
    graphComponent.graph.edges.forEach(edge => {
      if (!adjacentEdges.has(edge)) {
        edge.tag.isTransparentized = true;
      }
    });
  }

  return hoverMode;
};

class ArdoqHoverInputMode extends ItemHoverInputMode {
  override isValidHoverItem(item: IModelItem) {
    return item.tag && super.isValidHoverItem(item);
  }
}
